import CancelIcon from "@mui/icons-material/Clear";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import SearchIcon from "@mui/icons-material/Search";
import { CircularProgress, InputAdornment } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import { selectUserSession } from "../slices/userSession/userSessionSlice.js";
import { sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  ALL_TIME_OFF_STATUSES,
  ALL_TIME_OFF_TYPES,
  ADMIN_ALL_TIME_OFF_TYPES,
  LAST_UPDATED,
  MULTI_STATION_ADMIN_ACCESS,
  SHIFT,
} from "../util/constants.js";
import { THREE_YEARS_IN_PAST } from "../util/dateHelper";
import { convert_Luxon_To_DateFns } from "../util/dateTimeFormatter.js";
import { getScrubbedTimeOffRequestSnapshot } from "../util/timeOffRequestScrubber.js";
import StarsDatePicker from "./fundamentals/StarsDatePicker.jsx";
import TimeOffRequestDialog from "./TimeOffRequestDialog.jsx";

const useStyles = makeStyles((theme) => ({
  filterInputs: {
    width: "24.5vw",
    textAlign: "left",
  },
  adminFilterInputs: {
    width: "18vw",
    textAlign: "left",
  },
  topRow: {
    marginTop: "5px",
  },
  title: {
    fontSize: "30px",
    textAlign: "center",
    width: "200px",
    margin: "auto",
    marginTop: "25px",
    whiteSpace: "pre-line",
  },
}));

const TimeOffFilterFormDesktop = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);

  const getColumnNumberBasedOnUser = () => {
    return props.isAdminPage ? 3 : 4;
  };

  const getClassNameBasedOnUser = () => {
    return props.isAdminPage ? classes.adminFilterInputs : classes.filterInputs;
  };

  const renderDateTypeFilterMenuItems = () => {
    const dateTypes = [SHIFT, LAST_UPDATED];
    return dateTypes
      .slice()
      .sort()
      .map((dateType) => {
        return (
          <MenuItem key={dateType} value={dateType}>
            {dateType}
          </MenuItem>
        );
      });
  };

  const renderTimeOffTypeFilterMenuItems = () => {
    const timeOffTypes = props.isAdminPage ? ADMIN_ALL_TIME_OFF_TYPES : ALL_TIME_OFF_TYPES;
    return timeOffTypes
      .slice()
      .sort()
      .map((timeOffType) => {
        return (
          <MenuItem key={timeOffType} value={timeOffType}>
            <Checkbox color="primary" checked={props.timeOffTypeFilters.indexOf(timeOffType) > -1} />
            <ListItemText primary={timeOffType} />
          </MenuItem>
        );
      });
  };

  const renderStatusFilterMenuItems = () => {
    return ALL_TIME_OFF_STATUSES.slice()
      .sort()
      .map((status) => {
        return (
          <MenuItem key={status} value={status}>
            <Checkbox color="primary" checked={props.timeOffStatusFilters.indexOf(status) > -1} />
            <ListItemText primary={status} />
          </MenuItem>
        );
      });
  };

  const renderStationFilterMenuItems = () => {
    return (
      props.stations &&
      props.stations
        .slice()
        .sort()
        .map((station) => {
          return (
            <MenuItem key={station} value={station} disabled={!props.featureConfig.userStations.includes(station)}>
              {station}
            </MenuItem>
          );
        })
    );
  };

  const renderWorkgroupFilterMenuItems = () => {
    return (
      props.allWorkgroupFilters &&
      props.allWorkgroupFilters
        .slice()
        .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
        .map((workgroup) => {
          return (
            <MenuItem key={workgroup} value={workgroup}>
              <Checkbox color="primary" checked={props.workgroupFilters.indexOf(workgroup) > -1} />
              <ListItemText primary={workgroup} />
            </MenuItem>
          );
        })
    );
  };

  const SearchButton = () => {
    return (
      <IconButton
        size="small"
        className="search-icon-button"
        onMouseDown={props.onSearchSubmit.bind(this, props.searchString)}
      >
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelIconButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onSearchCancel.bind(this)}>
        <CancelIcon />
      </IconButton>
    );
  };

  const renderDateTypeSelectList = () => {
    return (
      <FormControl variant="outlined">
        <InputLabel>Date Type</InputLabel>
        <Select
          id="timeOffFormTypeSelectElement"
          className={getClassNameBasedOnUser()}
          value={props.dateTypeFilter}
          onChange={props.onDateTypeFilterChange.bind(this)}
          label="Date Type"
        >
          <MenuItem disabled>
            <em>Filter on Date Type</em>
          </MenuItem>
          {renderDateTypeFilterMenuItems()}
        </Select>
      </FormControl>
    );
  };

  const renderTimeOffTypeSelectList = () => {
    if (props.timeOffTypeFilters.length > 0) {
      return (
        <FormControl variant="outlined">
          <InputLabel>Time Off Type</InputLabel>
          <Select
            id="timeOffTypeSelectElement"
            className={getClassNameBasedOnUser()}
            label="Time Off Type"
            multiple={true}
            MenuProps={{ autoFocus: false }}
            value={props.timeOffTypeFilters}
            onChange={props.onTimeOffTypeFilterChange.bind(this)}
            renderValue={(selected) => {
              const timeOffTypes = props.isAdminPage ? ADMIN_ALL_TIME_OFF_TYPES : ALL_TIME_OFF_TYPES;
              return selected.length === timeOffTypes.length ? "ALL" : selected.length === 1 ? selected[0] : "MULTI";
            }}
          >
            <MenuItem disabled>
              <em>Filter on Time Off Type</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={props.isTimeOffTypeSelectAllChecked()}
                indeterminate={props.isTimeOffTypeSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderTimeOffTypeFilterMenuItems()}
          </Select>
        </FormControl>
      );
    } else {
      return (
        <FormControl variant="outlined">
          <InputLabel>Time Off Type</InputLabel>
          <Select
            error
            id="timeOffTypeSelectElement"
            className={getClassNameBasedOnUser()}
            label="Time Off Type"
            multiple={true}
            MenuProps={{ autoFocus: false }}
            value={props.timeOffTypeFilters}
            onChange={props.onTimeOffTypeFilterChange.bind(this)}
            renderValue={(selected) => {
              const timeOffTypes = props.isAdminPage ? ADMIN_ALL_TIME_OFF_TYPES : ALL_TIME_OFF_TYPES;
              return selected.length === timeOffTypes.length ? "ALL" : selected.length === 1 ? selected[0] : "MULTI";
            }}
          >
            <MenuItem disabled>
              <em>Filter on Time Off Type</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={props.isTimeOffTypeSelectAllChecked()}
                indeterminate={props.isTimeOffTypeSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderTimeOffTypeFilterMenuItems()}
          </Select>
          <FormHelperText error>At least one Time Off Type must be selected</FormHelperText>
        </FormControl>
      );
    }
  };

  const renderStartDatePicker = () => {
    return (
      <StarsDatePicker
        label="Start Date"
        date={props.startDateFilter}
        onChange={props.onStartDateFilterChange}
        dateErrors={props.startDateFilterErrors}
        minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
        className={getClassNameBasedOnUser()}
      />
    );
  };

  const renderEndDatePicker = () => {
    return (
      <StarsDatePicker
        label="End Date"
        date={props.endDateFilter}
        onChange={props.onEndDateFilterChange}
        dateErrors={props.endDateFilterErrors}
        className={getClassNameBasedOnUser()}
      />
    );
  };

  const renderTimeOffStatusSelectList = () => {
    if (props.timeOffStatusFilters.length > 0) {
      return (
        <FormControl variant="outlined">
          <InputLabel>Status</InputLabel>
          <Select
            id="timeOffStatusSelectElement"
            className={getClassNameBasedOnUser()}
            label="Status"
            multiple={true}
            MenuProps={{ autoFocus: false }}
            value={props.timeOffStatusFilters}
            onChange={props.onStatusFilterChange.bind(this)}
            renderValue={(selected) =>
              selected.length === ALL_TIME_OFF_STATUSES.length ? "ALL" : selected.length === 1 ? selected[0] : "MULTI"
            }
          >
            <MenuItem disabled>
              <em>Filter on Status</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={props.isStatusSelectAllChecked()}
                indeterminate={props.isStatusSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderStatusFilterMenuItems()}
          </Select>
        </FormControl>
      );
    } else {
      return (
        <FormControl variant="outlined">
          <InputLabel>Status</InputLabel>
          <Select
            error
            id="timeOffStatusSelectElement"
            className={getClassNameBasedOnUser()}
            label="Status"
            multiple={true}
            MenuProps={{ autoFocus: false }}
            value={props.timeOffStatusFilters}
            onChange={props.onStatusFilterChange.bind(this)}
            renderValue={(selected) =>
              selected.length === ALL_TIME_OFF_STATUSES.length ? "ALL" : selected.length === 1 ? selected[0] : "MULTI"
            }
          >
            <MenuItem disabled>
              <em>Filter on Status</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={props.isStatusSelectAllChecked()}
                indeterminate={props.isStatusSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderStatusFilterMenuItems()}
          </Select>
          <FormHelperText error>At least one Status must be selected</FormHelperText>
        </FormControl>
      );
    }
  };

  const renderStationDropDown = () => {
    return (
      <>
        {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
          <Box mt={1} mb={1}>
            <FormControl variant="outlined">
              <InputLabel>Station</InputLabel>
              <Select
                id="stationFilterSelectElement"
                className="filter-input"
                label="Station"
                value={props.stationFilter}
                onChange={props.onStationFilterChange}
              >
                <MenuItem disabled>
                  <em>Filter on Station</em>
                </MenuItem>
                {renderStationFilterMenuItems()}
              </Select>
            </FormControl>
          </Box>
        ) : (
          <Box mt={1} mb={1}>
            <TextField
              className="filter-input"
              label="Station"
              variant="outlined"
              value={props.stationFilter}
              disabled
            />
          </Box>
        )}
      </>
    );
  };

  const renderWorkgroupFilterDropDown = () => {
    return (
      <>
        {props.workgroupFilters && props.workgroupFilters.length > 0 ? (
          <FormControl variant="outlined">
            <InputLabel>Workgroup</InputLabel>
            <Select
              id="workgroupFilterSelectElement"
              className="filter-input"
              label="Workgroup"
              multiple={true}
              value={!!props.workgroupFilters ? props.workgroupFilters : []}
              MenuProps={{ autoFocus: false }}
              onChange={props.onWorkgroupFilterChange.bind(this)}
              renderValue={(selected) =>
                selected.length === props.allWorkgroupFilters.length
                  ? "ALL"
                  : selected.length === 1
                  ? selected[0]
                  : "MULTI"
              }
            >
              <MenuItem disabled>
                <em>Filter on Workgroup</em>
              </MenuItem>
              <MenuItem key="select-all" value="Select All">
                <Checkbox
                  color="primary"
                  checked={props.isWorkgroupSelectAllChecked()}
                  indeterminate={props.isWorkgroupSelectAllIndeterminate()}
                  indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {renderWorkgroupFilterMenuItems()}
            </Select>
          </FormControl>
        ) : (
          <FormControl variant="outlined">
            <InputLabel>Workgroup</InputLabel>
            <Select
              error
              id="workgroupFilterSelectElement"
              className="filter-input"
              label="Workgroup"
              multiple={true}
              value={!!props.workgroupFilters ? props.workgroupFilters : []}
              MenuProps={{ autoFocus: false }}
              onChange={props.onWorkgroupFilterChange.bind(this)}
              renderValue={(selected) =>
                selected.length === props.allWorkgroupFilters.length
                  ? "ALL"
                  : selected.length === 1
                  ? selected[0]
                  : "MULTI"
              }
            >
              <MenuItem disabled>
                <em>Filter on Workgroup</em>
              </MenuItem>
              <MenuItem key="select-all" value="Select All">
                <Checkbox
                  color="primary"
                  checked={props.isWorkgroupSelectAllChecked()}
                  indeterminate={props.isWorkgroupSelectAllIndeterminate()}
                  indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {renderWorkgroupFilterMenuItems()}
            </Select>
            <FormHelperText error>At least one Workgroup must be selected</FormHelperText>
          </FormControl>
        )}
      </>
    );
  };

  const renderSearchBar = () => {
    if (props.searchStringErrors !== "") {
      return (
        <FormControl>
          <TextField
            className={getClassNameBasedOnUser()}
            label="Search"
            variant="outlined"
            value={props.searchString}
            onChange={props.onSearchChange.bind(this)}
            onBlur={props.onSearchBlur.bind(this)}
            InputProps={{
              endAdornment:
                props.searchString !== "" ? (
                  <InputAdornment position="end">
                    {props.timeOffRequestFoundByReferenceNumberSearchIsLoading ? <CircularProgress size={16} /> : ""}
                    <CancelIconButton />
                    <SearchButton />
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <SearchButton />
                  </InputAdornment>
                ),
            }}
            onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
            error
            helperText={props.searchStringErrors}
          />
        </FormControl>
      );
    } else {
      return (
        <FormControl>
          <TextField
            className={getClassNameBasedOnUser()}
            label="Search"
            variant="outlined"
            value={props.searchString}
            onChange={props.onSearchChange.bind(this)}
            onBlur={props.onSearchBlur.bind(this)}
            InputProps={{
              endAdornment:
                props.searchString !== "" ? (
                  <InputAdornment position="end">
                    {props.timeOffRequestFoundByReferenceNumberSearchIsLoading ? <CircularProgress size={16} /> : ""}
                    <CancelIconButton />
                    <SearchButton />
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <SearchButton />
                  </InputAdornment>
                ),
            }}
            onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
          />
        </FormControl>
      );
    }
  };

  return (
    <Grid container className={classes.topRow} data-testid="time-off-filter-form-desktop">
      <Grid item xs={3}>
        <Typography component={"div"}>
          <Box className={classes.title}>{props.title}</Box>
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Grid container justifyContent="center" spacing={0}>
          <Grid item xs={getColumnNumberBasedOnUser()}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {renderDateTypeSelectList()}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {renderTimeOffTypeSelectList()}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={getColumnNumberBasedOnUser()}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {renderStartDatePicker()}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {renderTimeOffStatusSelectList()}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={getColumnNumberBasedOnUser()}>
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {renderEndDatePicker()}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {!!props.isAdminPage ? renderWorkgroupFilterDropDown() : renderSearchBar()}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {!!props.isAdminPage && (
            <Grid item xs={getColumnNumberBasedOnUser()}>
              <Grid container>
                <Grid item xs={12}>
                  <Box mt={1} mb={1}>
                    {renderStationDropDown()}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={1} mb={1}>
                    {renderSearchBar()}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      {props.timeOffRequestFoundByReferenceNumberSearchIsDialogOpen && (
        <TimeOffRequestDialog
          timeOffRequest={getScrubbedTimeOffRequestSnapshot(props.timeOffRequestFoundByReferenceNumberSearch)}
          isDialogOpen={props.timeOffRequestFoundByReferenceNumberSearchIsDialogOpen}
          handleClickDialogOpen={props.onClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen}
          handleClickDialogClose={props.onClickTimeOffRequestFoundByReferenceNumberSearchDialogClose}
          isAdminPage={props.isAdminPage}
          isDesktopView={props.isDesktopView}
        />
      )}
    </Grid>
  );
};

TimeOffFilterFormDesktop.propTypes = {
  title: PropTypes.string.isRequired,
  onDateTypeFilterChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  isStatusSelectAllChecked: PropTypes.func.isRequired,
  isStatusSelectAllIndeterminate: PropTypes.func.isRequired,
  onStartDateFilterChange: PropTypes.func.isRequired,
  onEndDateFilterChange: PropTypes.func.isRequired,
  onTimeOffTypeFilterChange: PropTypes.func.isRequired,
  isTimeOffTypeSelectAllChecked: PropTypes.func.isRequired,
  isTimeOffTypeSelectAllIndeterminate: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchBlur: PropTypes.func.isRequired,
  onSearchEnterKeyPressed: PropTypes.func.isRequired,
  onSearchCancel: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default TimeOffFilterFormDesktop;
